import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ALL_ROLE_CODES, type RoleCode, type CandidateDetail, type ConvertCandidateInput } from '@pms/shared';
import { recruitmentService } from '@/services/recruitment.service';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { cn } from '@/lib/utils';
import { getErrorMessage } from './utils';

export function ConvertCandidateModal({
  candidate, onClose, onConverted,
}: {
  candidate: CandidateDetail;
  onClose: () => void;
  onConverted: () => void;
}) {
  const queryClient = useQueryClient();
  const [employeeCode, setEmployeeCode] = useState('');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState<RoleCode[]>(['USER']);
  const [joiningDate, setJoiningDate] = useState('');

  const toggleRole = (r: RoleCode) =>
    setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));

  const convert = useMutation({
    mutationFn: () => {
      const payload: ConvertCandidateInput = {
        employee_code: employeeCode.trim(),
        password,
        role_codes: roles,
        department_id: candidate.applied_department_id,
        designation_id: candidate.applied_designation_id,
        joining_date: joiningDate,
      };
      return recruitmentService.convertCandidate(candidate.id, payload);
    },
    onSuccess: () => {
      toast.success('Candidate converted to employee. Joining process started.');
      void queryClient.invalidateQueries({ queryKey: ['recruitment'] });
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
      onConverted();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = employeeCode.trim() && password.length >= 8 && roles.length > 0 && joiningDate;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Convert to Employee</DialogTitle>
          <p className="text-sm text-muted-foreground">Create an employee account for {candidate.first_name} {candidate.last_name}</p>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Employee Code <span className="text-destructive">*</span></Label>
            <Input value={employeeCode} onChange={(e) => setEmployeeCode(e.target.value)} placeholder="e.g. EMP1042" />
          </div>
          <div className="space-y-1.5">
            <Label>Temporary Password <span className="text-destructive">*</span></Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" />
          </div>
          <div className="space-y-1.5">
            <Label>Joining Date <span className="text-destructive">*</span></Label>
            <DatePicker value={joiningDate} onChange={setJoiningDate} />
          </div>
          <div className="space-y-1.5">
            <Label>Roles <span className="text-destructive">*</span></Label>
            <div className="flex flex-wrap gap-2">
              {ALL_ROLE_CODES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleRole(r)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    roles.includes(r)
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-muted-foreground hover:bg-muted',
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" disabled={!canSubmit || convert.isPending} onClick={() => convert.mutate()}>
            {convert.isPending ? 'Converting…' : 'Convert'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
